import React, { useState } from 'react';
import { CheckIcon, XIcon } from 'lucide-react';
import { Avatar } from '../ui/Etiquetas';
import { inicialesDe } from '../../data/abogados';
import { MATERIAS } from '../../data/catalogos';
import { useDatos } from '../../contexts/DatosContext';
import type { Abogado, Materia, Rol } from '../../types';
const ROLES: Rol[] = ['Administrador', 'Abogado', 'Recepción/Archivo'];
const claseInput = 'h-9 w-full rounded border border-gris-200 px-3 text-sm text-gris-800 placeholder:text-gris-400 focus:border-marino-500';
const claseEtiqueta = 'block text-2xs font-semibold uppercase tracking-wide text-gris-500';
export function EditorAbogado({
  abogado,
  esNuevo,
  onGuardar,
  onCancelar
}: {
  abogado: Abogado;
  esNuevo: boolean;
  onGuardar: (abogado: Abogado) => void;
  onCancelar: () => void;
}) {
  const {
    sucursales,
    abogados
  } = useDatos();
  const [borrador, setBorrador] = useState<Abogado>(abogado);
  const [error, setError] = useState('');
  const cambiar = <C extends keyof Abogado,>(campo: C, valor: Abogado[C]) => setBorrador(prev => ({
    ...prev,
    [campo]: valor
  }));
  const alternarMateria = (m: Materia) => setBorrador(prev => ({
    ...prev,
    especialidades: prev.especialidades.includes(m) ? prev.especialidades.filter(x => x !== m) : [...prev.especialidades, m]
  }));
  const guardar = () => {
    if (!borrador.nombre.trim()) {
      setError('El nombre completo es obligatorio.');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(borrador.correo.trim())) {
      setError('Captura un correo válido.');
      return;
    }
    if (!borrador.sucursalId) {
      setError('Asigna una sucursal.');
      return;
    }
    const cedula = borrador.cedula.trim();
    if (borrador.rol === 'Abogado' && !cedula) {
      setError('Un abogado requiere cédula profesional.');
      return;
    }
    if (cedula && cedula !== 'No aplica' && abogados.some(a => a.id !== borrador.id && a.cedula === cedula)) {
      setError(`La cédula ${cedula} ya está registrada para otra persona.`);
      return;
    }
    if (borrador.rol === 'Abogado' && borrador.especialidades.length === 0) {
      setError('Selecciona al menos una materia que maneja.');
      return;
    }
    onGuardar({
      ...borrador,
      nombre: borrador.nombre.trim(),
      correo: borrador.correo.trim(),
      telefono: borrador.telefono.trim(),
      cedula: cedula || 'No aplica',
      foto: borrador.foto?.trim() || undefined
    });
  };
  return <div className="border-b border-marino-200 bg-marino-50 px-5 py-5" data-id="element-37" data-mp="3gqv7o99cgn7e">
      <h2 className="text-sm font-semibold text-marino-900" data-id="element-38" data-mp="3lqr9c1ukk2js">
        {esNuevo ? 'Agregar abogado o personal' : `Editar «${abogado.nombre}»`}
      </h2>

      <div className="mt-4 grid gap-5 lg:grid-cols-[auto_1fr]" data-id="element-39" data-mp="3prt0rwi8vab7">
        <div className="flex flex-col items-center gap-2" data-id="element-40" data-mp="1rd84e6y7lhog">
          <Avatar iniciales={inicialesDe(borrador.nombre || 'NN')} foto={borrador.foto} tamano="lg" nombre={borrador.nombre} data-id="element-41" data-mp="1ret4ec3alstp" />
          <span className="text-2xs text-gris-400" data-id="element-42" data-mp="1nw3nva8bubkz">Vista previa</span>
        </div>

        <div className="grid gap-4 md:grid-cols-3" data-id="element-43" data-mp="1j3ammwc2hldn">
          <label className="md:col-span-2" data-id="element-44" data-mp="1e4ptthz5a3z8">
            <span className={claseEtiqueta} data-id="element-45" data-mp="1e5sh7fx9doae">Nombre completo *</span>
            <input autoFocus className={`mt-1.5 ${claseInput}`} value={borrador.nombre} onChange={e => cambiar('nombre', e.target.value)} placeholder="Lic. Nombre Apellido Apellido" data-id="element-46" data-mp="1a3ximj6ppp0b" />
          </label>
          <label data-id="element-47" data-mp="1xsq7jvox2wlf">
            <span className={claseEtiqueta} data-id="element-48" data-mp="1xs72hfkd82ot">Fecha de ingreso</span>
            <input type="date" className={`tabular mt-1.5 ${claseInput}`} value={borrador.fechaIngreso} onChange={e => cambiar('fechaIngreso', e.target.value)} data-id="element-49" data-mp="1stvrv2m8xo5j" />
          </label>

          <label className="md:col-span-3" data-id="element-50" data-mp="hdxdcj8au9wx">
            <span className={claseEtiqueta} data-id="element-51" data-mp="hh2zbplmsvof">Foto (URL)</span>
            <input className={`mt-1.5 ${claseInput}`} value={borrador.foto ?? ''} onChange={e => cambiar('foto', e.target.value)} placeholder="https://… (si se deja vacío se usan las iniciales)" data-id="element-52" data-mp="259v6hg8b1huw" />
          </label>

          <label data-id="element-53" data-mp="20dd4go4czxm9">
            <span className={claseEtiqueta} data-id="element-54" data-mp="20cuxoxk2m103">Correo *</span>
            <input type="email" className={`mt-1.5 ${claseInput}`} value={borrador.correo} onChange={e => cambiar('correo', e.target.value)} placeholder="nombre@limaamaro.mx" data-id="element-55" data-mp="2o1eanqe2f1yi" />
          </label>
          <label data-id="element-56" data-mp="2k0aqwsswcls0">
            <span className={claseEtiqueta} data-id="element-57" data-mp="2jzsy665g0pou">Teléfono</span>
            <input className={`tabular mt-1.5 ${claseInput}`} value={borrador.telefono} onChange={e => cambiar('telefono', e.target.value)} placeholder="229 118 4400" data-id="element-58" data-mp="2k2f4y3kif160" />
          </label>
          <label data-id="element-59" data-mp="1033qahpp6r4h">
            <span className={claseEtiqueta} data-id="element-60" data-mp="102k5mt8nxomr">Cédula profesional</span>
            <input className={`tabular mt-1.5 ${claseInput}`} value={borrador.cedula} onChange={e => cambiar('cedula', e.target.value)} placeholder="8451237" data-id="element-61" data-mp="w3be7v13i2ud" />
          </label>

          <label data-id="element-62" data-mp="151586zbe0ked">
            <span className={claseEtiqueta} data-id="element-63" data-mp="hdnayo9csu40">Sucursal *</span>
            <select className={`mt-1.5 ${claseInput}`} value={borrador.sucursalId} onChange={e => cambiar('sucursalId', e.target.value)} data-id="element-64" data-mp="he6ta62x1kik">
              <option value="" data-id="element-65" data-mp="r51hhfzq2u6n">Selecciona</option>
              {sucursales.filter(s => s.activa).map(s => <option key={s.id} value={s.id} data-id="element-66" data-mp="3usa6izyme4nc">
                    {s.nombre} ({s.clave})
                  </option>)}
            </select>
          </label>
          <label data-id="element-67" data-mp="3prcqasx67koh">
            <span className={claseEtiqueta} data-id="element-68" data-mp="1tqc1lrw83xd5">Rol en el sistema *</span>
            <select className={`mt-1.5 ${claseInput}`} value={borrador.rol} onChange={e => cambiar('rol', e.target.value as Rol)} data-id="element-69" data-mp="1tq2dk9uqo0a3">
              {ROLES.map(r => <option key={r} value={r} data-id="element-70" data-mp="27hpiey2qyh4h">
                  {r}
                </option>)}
            </select>
          </label>
          <label data-id="element-71" data-mp="1ashtrh6tzrwc">
            <span className={claseEtiqueta} data-id="element-72" data-mp="1at16mjhmjq3i">Estado</span>
            <select className={`mt-1.5 ${claseInput}`} value={borrador.activo ? 'activo' : 'inactivo'} onChange={e => cambiar('activo', e.target.value === 'activo')} data-id="element-73" data-mp="1fq1aqouwyirz">
              <option value="activo" data-id="element-74" data-mp="1os0sxkhnzswi">Activo</option>
              <option value="inactivo" data-id="element-75" data-mp="2v7bgtjbaosbf">Inactivo (conserva historial)</option>
            </select>
          </label>

          <fieldset className="md:col-span-3" data-id="element-76" data-mp="305v53qdd2g6y">
            <legend className={claseEtiqueta} data-id="element-77" data-mp="304jpmtajzk00">Materias que maneja</legend>
            <div className="mt-2 flex flex-wrap gap-2" data-id="element-78" data-mp="3a99x1eoq7gbm">
              {MATERIAS.map(m => {
              const activa = borrador.especialidades.includes(m);
              return <button key={m} type="button" onClick={() => alternarMateria(m)} aria-pressed={activa} className={`h-8 rounded border px-3 text-xs font-medium transition-colors duration-150 ${activa ? 'border-marino-800 bg-marino-800 text-white' : 'border-gris-200 bg-white text-gris-600 hover:border-marino-300'}`} data-id="element-79" data-mp="30jqbg19mp8d2">
                    {m}
                  </button>;
            })}
            </div>
          </fieldset>
        </div>
      </div>

      {error && <p role="alert" className="mt-3 text-xs font-medium text-peligro-600" data-id="element-80" data-mp="bsfpkjj2o2i3">
          {error}
        </p>}

      <div className="mt-4 flex justify-end gap-2" data-id="element-81" data-mp="v2qo6etsj0jj">
        <button type="button" onClick={onCancelar} className="inline-flex h-9 items-center gap-1.5 rounded border border-gris-200 bg-white px-3 text-sm font-medium text-gris-600 transition-colors duration-150 hover:border-gris-300" data-id="element-82" data-mp="z313wj7dx5bl">
          <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-83" data-mp="q8w4hbe6qlkg" />
          Cancelar
        </button>
        <button type="button" onClick={guardar} className="inline-flex h-9 items-center gap-1.5 rounded bg-marino-800 px-3.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-marino-700" data-id="element-84" data-mp="bc54p10ln7ub">
          <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-85" data-mp="7bcx0c7dlb05" />
          {esNuevo ? 'Agregar' : 'Guardar cambios'}
        </button>
      </div>
    </div>;
}